<template>
  <AIVDialog
    v-model="isVisible"
    title="Edit Shift"
    width="40%"
    height="85vh"
    class="mobile-friendly-modal edit-clock-out-modal"
  >
    <div class="edit-clock-out-content">
      <!-- Shift Info Display -->
      <div class="mb-3">
        <h5>Shift Details</h5>
        <p>
          <strong>Date:</strong>
          {{ shift && shift.date ? formatDate(shift.date) : "Loading..." }}
        </p>
        <p>
          <strong>Scheduled Time:</strong>
          {{
            formatTimeRange(
              shift?.scheduledStartTime ||
                shift?.originalData?.scheduled_start ||
                shift?.startTime ||
                shift?.scheduled_start,
              shift?.scheduledEndTime ||
                shift?.originalData?.scheduled_end ||
                shift?.endTime ||
                shift?.scheduled_end,
              shift?.date,
            )
          }}
        </p>
        <p>
          <strong>Caregiver:</strong>
          <template v-if="isGroupShift && shift && shift.caregivers && shift.caregivers.length > 1">
            {{ caregiverOptions.map(c => c.name).filter(n => n && n !== 'Unknown').join(', ') || 'Multiple caregivers' }}
          </template>
          <template v-else>
            {{ shift ? formatCaregiver(shift) : "Loading..." }}
          </template>
        </p>
        <p v-if="shift && shift.clients && shift.clients.length > 0">
          <strong>Client:</strong>
          {{ shift.clients.map(c => c.name || `${c.first_name || ''} ${c.last_name || ''}`.trim()).filter(n => n).join(', ') || 'Unassigned' }}
        </p>
        <p>
          <strong>Status:</strong>
          <span
            v-if="shift && (shift.status || shift.shift_status)"
            :class="[
              'status-badge',
              getStatusClass(shift.status || shift.shift_status),
            ]"
            >{{ formatStatus(shift.status || shift.shift_status) }}</span
          ><span v-else>Loading...</span>
        </p>
      </div>

      <!-- Payer Selection -->
      <div class="mb-3">
        <label class="form-label">Payer:</label>
        <AIVSelect
          :value="selectedPayer"
          @input="selectedPayer = $event"
          :options="payerOptions.map((p) => ({ id: p, name: p }))"
          placeholder="Select payer"
          :disabled="isGroupShift"
          :custom-style="{
            backgroundColor: '#656565',
            minWidth: '100%',
          }"
        />
      </div>

      <!-- Client Selection (VR only) -->
      <div v-if="selectedPayer === 'VR'" class="mb-3">
        <label class="form-label">Client:</label>
        <AIVSelect
          :value="selectedClientId"
          @input="handleClientChange"
          :options="availableClients.map((c) => ({ id: c.id, name: c.name }))"
          placeholder="Select client"
          :disabled="loadingClients"
          :custom-style="{
            backgroundColor: '#656565',
            minWidth: '100%',
          }"
        />
      </div>

      <!-- Service Code Selection -->
      <div class="mb-3">
        <label class="form-label">Service Code:</label>
        <AIVSelect
          :value="selectedServiceCodeId"
          @input="selectedServiceCodeId = $event"
          :options="
            availableServiceCodes.map((sc) => ({
              id: sc.id,
              name:
                selectedPayer === 'Non Service' ? sc.name : sc.code || sc.name,
            }))
          "
          placeholder="Select service code"
          :disabled="
            loadingServiceCodes ||
            availableServiceCodes.length === 0 ||
            ((selectedPayer === 'VR' || selectedPayer === 'DSPD') && !selectedClientId)
          "
          :custom-style="{
            backgroundColor: '#656565',
            minWidth: '100%',
          }"
        />
        <div
          v-if="
            !loadingServiceCodes && (selectedPayer === 'VR' || selectedPayer === 'DSPD') && !selectedClientId
          "
          class="service-code-warning"
        >
          <small class="text-warning">
            <i class="fa-solid fa-exclamation-triangle"></i>
            Please select a client first to see available service codes
          </small>
        </div>
        <div
          v-else-if="!loadingServiceCodes && availableServiceCodes.length === 0"
          class="service-code-warning"
        >
          <small class="text-warning">
            <i class="fa-solid fa-exclamation-triangle"></i>
            No service codes available for selected payer
          </small>
        </div>
      </div>

      <!-- Per-Caregiver Clock Times (Group Shifts) -->
      <div v-if="isGroupShift" class="mb-4">
        <h5>Caregiver Clock Times</h5>
        <div class="caregiver-clock-times">
          <div
            v-for="cg in caregiverClockData"
            :key="cg.caregiverId"
            class="caregiver-clock-row"
          >
            <div class="caregiver-name">{{ cg.caregiverName }}</div>
            <div class="caregiver-time-inputs">
              <div class="caregiver-time-field">
                <span class="clock-label">In:</span>
                <AIVTimeSelect
                  v-model="cg.clockIn"
                  placeholder="Clock in"
                  class="clock-time-picker-sm"
                  :allowExactMinutes="true"
                  :disabled="Number(cg.caregiverId) === Number(loginStore.user?.id)"
                />
              </div>
              <div class="caregiver-time-field">
                <span class="clock-label">Out:</span>
                <AIVTimeSelect
                  v-model="cg.clockOut"
                  placeholder="Clock out"
                  class="clock-time-picker-sm"
                  :allowExactMinutes="true"
                  :disabled="Number(cg.caregiverId) === Number(loginStore.user?.id)"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Single Clock In/Out (Non-Group Shifts) -->
      <template v-if="!isGroupShift">
        <!-- Clock In Time Input -->
        <div class="mb-3">
          <label class="form-label">Clock In Time:</label>
          <div class="time-input-container">
            <AIVTimeSelect
              v-model="newClockInTime"
              placeholder="Select clock in time"
              class="clock-time-picker"
              :allowExactMinutes="true"
            />
          </div>
        </div>

        <!-- Clock Out Time Input -->
        <div class="mb-3">
          <label class="form-label">Clock Out Time:</label>
          <div class="time-input-container">
            <AIVTimeSelect
              v-model="newClockOutTime"
              placeholder="Select clock out time"
              class="clock-time-picker"
              :allowExactMinutes="true"
            />
          </div>
        </div>

        <!-- Shadow/training caregivers' own clock rows (non-group).
             Their times never affect the shift-level clock or billing;
             approval is blocked while one is still open. -->
        <div v-if="sideClockData.length > 0" class="mb-4">
          <h5>Shadow / Training Clock Times</h5>
          <div class="caregiver-clock-times">
            <div
              v-for="cg in sideClockData"
              :key="'side-' + cg.caregiverId"
              class="caregiver-clock-row"
            >
              <div class="caregiver-name">{{ cg.caregiverName }}</div>
              <div class="caregiver-time-inputs">
                <div class="caregiver-time-field">
                  <span class="clock-label">In:</span>
                  <AIVTimeSelect
                    v-model="cg.clockIn"
                    placeholder="Clock in"
                    class="clock-time-picker-sm"
                    :allowExactMinutes="true"
                  />
                </div>
                <div class="caregiver-time-field">
                  <span class="clock-label">Out:</span>
                  <AIVTimeSelect
                    v-model="cg.clockOut"
                    placeholder="Clock out"
                    class="clock-time-picker-sm"
                    :allowExactMinutes="true"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Break Start Time Input -->
        <div v-if="shouldShowBreakFields" class="mb-3">
          <label class="form-label">Break Start Time:</label>
          <div class="time-input-container">
            <AIVTimeSelect
              v-model="breakStartTime"
              placeholder="Select break start time"
              class="clock-time-picker"
              :allowExactMinutes="true"
            />
          </div>
        </div>

        <!-- Break End Time Input -->
        <div v-if="shouldShowBreakFields" class="mb-3">
          <label class="form-label">Break End Time:</label>
          <div class="time-input-container">
            <AIVTimeSelect
              v-model="breakEndTime"
              placeholder="Select break end time"
              class="clock-time-picker"
              :allowExactMinutes="true"
            />
          </div>
        </div>
      </template>

      <!-- Client Tasks Section (Group Shifts) -->
      <div v-if="isGroupShift && clientTaskData.length > 0" class="mb-4">
        <h5>Client Tasks</h5>
        <div class="client-tasks-list">
          <div
            v-for="task in clientTaskData"
            :key="task.id"
            class="client-task-card"
          >
            <div class="client-task-header">
              {{ task.clientName }}
              <span v-if="task.didNotAttend" class="did-not-attend-badge">
                <i class="fa-solid fa-user-xmark"></i> Did Not Attend
              </span>
            </div>
            <template v-if="!task.didNotAttend">
            <div class="client-task-rows">
              <!-- Pick Up -->
              <div class="client-task-row">
                <div class="client-task-info">
                  <i class="fa-solid fa-car-side"></i>
                  <span class="client-task-label">Pick Up</span>
                </div>
                <div class="client-task-controls">
                  <AIVSelect
                    :value="task.pickupCaregiverId"
                    @input="task.pickupCaregiverId = $event"
                    :options="caregiverOptions"
                    placeholder="Assign caregiver"
                    :custom-style="{ backgroundColor: '#656565', minWidth: '160px' }"
                  />
                  <AIVTimeSelect
                    v-model="task.pickupTimeLocal"
                    placeholder="Pick up time"
                    class="clock-time-picker-sm"
                    :allowExactMinutes="true"
                  />
                </div>
              </div>
              <!-- Pick Up Location -->
              <div class="client-task-row location-row">
                <div class="client-task-info">
                  <i class="fa-solid fa-location-dot"></i>
                  <span class="client-task-label">Pick Up Location</span>
                </div>
                <div class="client-task-controls location-controls">
                  <AIVSelect
                    v-if="getAddressOptions(task.clientId).length > 0"
                    :value="task.pickupAddressId"
                    @input="task.pickupAddressId = $event"
                    :options="getAddressOptions(task.clientId)"
                    placeholder="Select address"
                    :custom-style="{ backgroundColor: '#656565', width: '100%' }"
                  />
                  <el-button
                    v-if="!isAddingAddress(task.clientId, 'pickup')"
                    type="text"
                    size="small"
                    class="add-address-btn"
                    @click="startAddAddress(task.clientId, 'pickup')"
                  >
                    <i class="fa-solid fa-plus"></i> Add New
                  </el-button>
                </div>
                <div v-if="isAddingAddress(task.clientId, 'pickup')" class="new-address-form">
                  <div class="address-form-row">
                    <el-input v-model="newAddress.address1" placeholder="Address 1" size="small" />
                  </div>
                  <div class="address-form-row">
                    <el-input v-model="newAddress.address2" placeholder="Address 2 (optional)" size="small" />
                  </div>
                  <div class="address-form-row three-col">
                    <el-input v-model="newAddress.city" placeholder="City" size="small" />
                    <el-input v-model="newAddress.state" placeholder="State" size="small" class="state-input" />
                    <el-input v-model="newAddress.zip" placeholder="Zip" size="small" class="zip-input" />
                  </div>
                  <div class="address-form-actions">
                    <el-button size="small" @click="cancelAddAddress">Cancel</el-button>
                    <el-button
                      type="primary"
                      size="small"
                      :loading="savingAddress"
                      :disabled="!newAddress.address1 || !newAddress.city || !newAddress.state"
                      @click="saveNewAddress(task, 'pickup')"
                    >
                      Save Address
                    </el-button>
                  </div>
                </div>
              </div>
              <!-- Drop Off -->
              <div class="client-task-row">
                <div class="client-task-info">
                  <i class="fa-solid fa-location-dot"></i>
                  <span class="client-task-label">Drop Off</span>
                </div>
                <div class="client-task-controls">
                  <AIVSelect
                    :value="task.dropoffCaregiverId"
                    @input="task.dropoffCaregiverId = $event"
                    :options="caregiverOptions"
                    placeholder="Assign caregiver"
                    :custom-style="{ backgroundColor: '#656565', minWidth: '160px' }"
                  />
                  <AIVTimeSelect
                    v-model="task.dropoffTimeLocal"
                    placeholder="Drop off time"
                    class="clock-time-picker-sm"
                    :allowExactMinutes="true"
                  />
                </div>
              </div>
              <!-- Drop Off Location -->
              <div class="client-task-row location-row">
                <div class="client-task-info">
                  <i class="fa-solid fa-location-dot"></i>
                  <span class="client-task-label">Drop Off Location</span>
                </div>
                <div class="client-task-controls location-controls">
                  <AIVSelect
                    v-if="getAddressOptions(task.clientId).length > 0"
                    :value="task.dropoffAddressId"
                    @input="task.dropoffAddressId = $event"
                    :options="getAddressOptions(task.clientId)"
                    placeholder="Select address"
                    :custom-style="{ backgroundColor: '#656565', width: '100%' }"
                  />
                  <el-button
                    v-if="!isAddingAddress(task.clientId, 'dropoff')"
                    type="text"
                    size="small"
                    class="add-address-btn"
                    @click="startAddAddress(task.clientId, 'dropoff')"
                  >
                    <i class="fa-solid fa-plus"></i> Add New
                  </el-button>
                </div>
                <div v-if="isAddingAddress(task.clientId, 'dropoff')" class="new-address-form">
                  <div class="address-form-row">
                    <el-input v-model="newAddress.address1" placeholder="Address 1" size="small" />
                  </div>
                  <div class="address-form-row">
                    <el-input v-model="newAddress.address2" placeholder="Address 2 (optional)" size="small" />
                  </div>
                  <div class="address-form-row three-col">
                    <el-input v-model="newAddress.city" placeholder="City" size="small" />
                    <el-input v-model="newAddress.state" placeholder="State" size="small" class="state-input" />
                    <el-input v-model="newAddress.zip" placeholder="Zip" size="small" class="zip-input" />
                  </div>
                  <div class="address-form-actions">
                    <el-button size="small" @click="cancelAddAddress">Cancel</el-button>
                    <el-button
                      type="primary"
                      size="small"
                      :loading="savingAddress"
                      :disabled="!newAddress.address1 || !newAddress.city || !newAddress.state"
                      @click="saveNewAddress(task, 'dropoff')"
                    >
                      Save Address
                    </el-button>
                  </div>
                </div>
              </div>
              <!-- Shift Notes -->
              <div class="client-task-row">
                <div class="client-task-info">
                  <i class="fa-solid fa-clipboard"></i>
                  <span class="client-task-label">Shift Notes</span>
                </div>
                <div class="client-task-controls">
                  <span v-if="task.shiftNotesCompleted || isClientNotesComplete(task.clientId)" class="client-task-completed">
                    {{ getTaskCaregiverName(task.shiftNotesCaregiverId || caregiverClockData[0]?.caregiverId) }} - Completed
                  </span>
                  <span v-else class="client-task-pending">Not completed</span>
                </div>
              </div>
              <!-- Medications -->
              <div v-if="task.medicationsCaregiverId != null" class="client-task-row">
                <div class="client-task-info">
                  <i class="fa-solid fa-pills"></i>
                  <span class="client-task-label">Medications</span>
                </div>
                <div class="client-task-controls">
                  <AIVSelect
                    :value="task.medicationsCaregiverId"
                    @input="task.medicationsCaregiverId = $event"
                    :options="caregiverOptions"
                    placeholder="Assign caregiver"
                    :custom-style="{ backgroundColor: '#656565', minWidth: '160px' }"
                  />
                  <span v-if="task.medicationsCompleted" class="client-task-completed">Completed</span>
                  <span v-else class="client-task-pending">Not completed</span>
                </div>
              </div>
            </div>

            <!-- Shift Notes -->
            <div class="client-notes-inline">
              <!-- Service Code Questions -->
              <div
                v-for="question in serviceCodeQuestions"
                :key="'q-' + question.id"
                class="mb-3"
              >
                <label class="form-label">
                  {{ question.question_text }}
                  <span v-if="question.required" class="required-indicator">*</span>
                </label>
                <div
                  v-if="question.description"
                  class="shift-notes-description mb-2"
                  style="white-space: pre-line;"
                >{{ question.description }}</div>
                <el-input
                  v-if="question.question_type === 'free_response' || !question.question_type"
                  type="textarea"
                  :rows="2"
                  :value="getQuestionAnswer(task.clientId, question.id)"
                  @input="setQuestionAnswer(task.clientId, question.id, $event)"
                  placeholder="Enter your answer..."
                />
                <div v-else-if="question.question_type === 'yes_no'" class="yes-no-buttons">
                  <el-button
                    size="medium"
                    :type="getQuestionAnswer(task.clientId, question.id) === 'yes' ? 'primary' : 'default'"
                    @click="setQuestionAnswer(task.clientId, question.id, 'yes')"
                  >Yes</el-button>
                  <el-button
                    size="medium"
                    :type="getQuestionAnswer(task.clientId, question.id) === 'no' ? 'danger' : 'default'"
                    @click="setQuestionAnswer(task.clientId, question.id, 'no')"
                  >No</el-button>
                </div>
                <AIVSelect
                  v-else-if="question.question_type === 'select'"
                  :value="getQuestionAnswer(task.clientId, question.id)"
                  @input="setQuestionAnswer(task.clientId, question.id, $event)"
                  :options="(question.options || []).map((o, i) => ({ id: o, name: o }))"
                  placeholder="Select an answer"
                  :custom-style="{ width: '100%' }"
                />
                <AIVSelect
                  v-else-if="question.question_type === 'multi_select'"
                  :value="parseMultiAnswer(getQuestionAnswer(task.clientId, question.id))"
                  @input="setQuestionAnswer(task.clientId, question.id, joinMultiAnswer($event))"
                  :options="(question.options || []).map((o) => ({ id: o, name: o }))"
                  placeholder="Select all that apply"
                  :multi-select="true"
                  :searchable="true"
                  :custom-style="{ width: '100%' }"
                />
              </div>

              <!-- Client Goals -->
              <div
                v-for="goal in getClientGoals(task.clientId)"
                :key="'g-' + goal.id"
                class="mb-3"
              >
                <label class="form-label">{{ goal.question_text }}</label>
                <el-input
                  v-if="goal.question_type === 'free_response' || !goal.question_type"
                  type="textarea"
                  :rows="2"
                  :value="getGoalAnswer(task.clientId, goal.id)"
                  @input="setGoalAnswer(task.clientId, goal.id, $event)"
                  placeholder="Enter your answer..."
                />
                <div v-else-if="goal.question_type === 'yes_no'" class="yes-no-buttons">
                  <el-button
                    size="medium"
                    :type="getGoalAnswer(task.clientId, goal.id) === 'yes' ? 'primary' : 'default'"
                    @click="setGoalAnswer(task.clientId, goal.id, 'yes')"
                  >Yes</el-button>
                  <el-button
                    size="medium"
                    :type="getGoalAnswer(task.clientId, goal.id) === 'no' ? 'danger' : 'default'"
                    @click="setGoalAnswer(task.clientId, goal.id, 'no')"
                  >No</el-button>
                </div>
              </div>

              <!-- Concerns -->
              <div class="mb-3">
                <label class="form-label">Do you have any concerns about this individual today? <span class="required-indicator">*</span></label>
                <el-input
                  type="textarea"
                  :rows="3"
                  :value="getClientShiftNotes(task.clientId)"
                  @input="setClientShiftNotes(task.clientId, $event)"
                  placeholder="Enter your answer..."
                />
              </div>
            </div>

            <!-- Medication Administration Logs -->
            <div v-if="getClientMedicationLogs(task.clientId).length" class="client-notes-inline">
              <strong>Medication Administration</strong>
              <div
                v-for="log in getClientMedicationLogs(task.clientId)"
                :key="'med-' + log.id"
                class="mb-2"
              >
                <div class="d-flex justify-content-between align-items-center">
                  <span>{{ log.medication ? log.medication.name : 'Unknown Medication' }}</span>
                  <span :class="log.was_administered ? 'client-task-completed' : 'client-task-pending'">
                    {{ log.was_administered ? 'Given' : 'Skipped' }}
                    <span v-if="log.skip_reason"> - {{ log.skip_reason }}</span>
                  </span>
                </div>
              </div>
            </div>
            </template>
          </div>
        </div>
      </div>

      <!-- DSPD 1-on-1 Shift Notes Section -->
      <div v-if="isDSPDOneOnOneShift" class="mb-4">
        <h5>Shift Notes</h5>

        <!-- Service Code Questions -->
        <div
          v-for="question in visibleDspdQuestions"
          :key="'oneone-q-' + question.id"
          class="mb-3"
        >
          <label class="form-label">
            {{ question.question_text }}
            <span v-if="question.required" class="required-indicator">*</span>
          </label>
          <div
            v-if="question.description"
            class="shift-notes-description mb-2"
            style="white-space: pre-line;"
          >{{ question.description }}</div>
          <el-input
            v-if="question.question_type === 'free_response' || !question.question_type"
            type="textarea"
            :rows="2"
            v-model="dspdOneOnOneQuestionAnswers[question.id]"
            placeholder="Enter your answer..."
          />
          <div v-else-if="question.question_type === 'yes_no'" class="yes-no-buttons">
            <el-button
              size="medium"
              :type="dspdOneOnOneQuestionAnswers[question.id] === 'yes' ? 'primary' : 'default'"
              @click="$set(dspdOneOnOneQuestionAnswers, question.id, 'yes')"
            >Yes</el-button>
            <el-button
              size="medium"
              :type="dspdOneOnOneQuestionAnswers[question.id] === 'no' ? 'danger' : 'default'"
              @click="$set(dspdOneOnOneQuestionAnswers, question.id, 'no')"
            >No</el-button>
          </div>
        </div>

        <!-- Client Goals -->
        <div
          v-for="goal in dspdOneOnOneGoals"
          :key="'oneone-g-' + goal.id"
          class="mb-3"
        >
          <label class="form-label">{{ goal.question_text }}</label>
          <el-input
            v-if="goal.question_type === 'free_response' || !goal.question_type"
            type="textarea"
            :rows="2"
            v-model="dspdOneOnOneGoalAnswers[goal.id]"
            placeholder="Enter your answer..."
          />
          <div v-else-if="goal.question_type === 'yes_no'" class="yes-no-buttons">
            <el-button
              size="medium"
              :type="dspdOneOnOneGoalAnswers[goal.id] === 'yes' ? 'primary' : 'default'"
              @click="$set(dspdOneOnOneGoalAnswers, goal.id, 'yes')"
            >Yes</el-button>
            <el-button
              size="medium"
              :type="dspdOneOnOneGoalAnswers[goal.id] === 'no' ? 'danger' : 'default'"
              @click="$set(dspdOneOnOneGoalAnswers, goal.id, 'no')"
            >No</el-button>
          </div>
        </div>

        <!-- Concerns (legacy shifts and question-less codes only) -->
        <div v-if="showConcernsField" class="mb-3">
          <label class="form-label">Do you have any concerns about this individual today? <span class="required-indicator">*</span></label>
          <el-input
            type="textarea"
            :rows="3"
            v-model="dspdOneOnOneConcerns"
            placeholder="Enter your answer..."
          />
        </div>
      </div>

      <!-- VR 1-on-1 service-code questions (editable, mirrors EditShiftNotesModal) -->
      <div v-if="isVRShift && vrServiceCodeQuestions.length" class="mb-4">
        <h5>Shift Note Questions</h5>
        <div
          v-for="q in vrServiceCodeQuestions"
          :key="'vr-q-' + q.id"
          class="mb-3"
        >
          <label class="form-label">
            {{ q.question_text }}
            <span v-if="q.required" class="required-indicator">*</span>
          </label>
          <div
            v-if="q.description"
            class="shift-notes-description mb-2"
            style="white-space: pre-line;"
          >{{ q.description }}</div>
          <el-input
            v-if="q.question_type === 'free_response' || !q.question_type"
            type="textarea"
            :rows="2"
            :value="vrQuestionAnswers[q.id] || ''"
            @input="$set(vrQuestionAnswers, q.id, $event)"
            placeholder="Enter your answer..."
          />
          <div v-else-if="q.question_type === 'yes_no'" class="yes-no-buttons">
            <el-button
              size="medium"
              :type="vrQuestionAnswers[q.id] === 'yes' ? 'primary' : 'default'"
              @click="$set(vrQuestionAnswers, q.id, 'yes')"
            >Yes</el-button>
            <el-button
              size="medium"
              :type="vrQuestionAnswers[q.id] === 'no' ? 'danger' : 'default'"
              @click="$set(vrQuestionAnswers, q.id, 'no')"
            >No</el-button>
          </div>
          <AIVSelect
            v-else-if="q.question_type === 'select'"
            :value="vrQuestionAnswers[q.id] || ''"
            @input="$set(vrQuestionAnswers, q.id, $event)"
            :options="(q.options || []).map((o) => ({ id: o, name: o }))"
            placeholder="Select an answer"
            :custom-style="{ width: '100%' }"
          />
          <AIVSelect
            v-else-if="q.question_type === 'multi_select'"
            :value="parseMultiAnswer(vrQuestionAnswers[q.id])"
            @input="$set(vrQuestionAnswers, q.id, joinMultiAnswer($event))"
            :options="(q.options || []).map((o) => ({ id: o, name: o }))"
            placeholder="Select all that apply"
            :multi-select="true"
            :searchable="true"
            :custom-style="{ width: '100%' }"
          />
        </div>
      </div>

      <!-- Mileage Tracking (shown if transportation was flagged on shift) -->
      <div v-if="shiftHasTransport" class="mileage-section mb-4">
        <h5><i class="fa-solid fa-car mr-1"></i> Mileage Tracking</h5>
        <div class="row">
          <div class="col-md-6 mb-2">
            <label class="form-label">Start Location</label>
            <el-input
              v-model="mileageStartLocation"
              placeholder="e.g. Office, Client's home"
              size="small"
            />
          </div>
          <div class="col-md-6 mb-2">
            <label class="form-label">Places Visited In Between</label>
            <el-input
              v-model="mileageMidLocation"
              placeholder="e.g. Work site, School"
              size="small"
            />
          </div>
        </div>
        <div class="row">
          <div class="col-md-6 mb-2">
            <label class="form-label">End Location</label>
            <el-input
              v-model="mileageEndLocation"
              placeholder="e.g. Client's home"
              size="small"
            />
          </div>
        </div>
        <div class="row">
          <div class="col-md-6 mb-2">
            <label class="form-label">Odometer Start</label>
            <el-input
              v-model="odometerStart"
              type="number"
              placeholder="Starting odometer reading"
              size="small"
            />
          </div>
          <div class="col-md-6 mb-2">
            <label class="form-label">Odometer End</label>
            <el-input
              v-model="odometerEnd"
              type="number"
              placeholder="Ending odometer reading"
              size="small"
            />
          </div>
        </div>
        <div v-if="computedMiles !== null" class="row">
          <div class="col-md-6 mb-2">
            <label class="form-label">Total Miles</label>
            <div class="computed-miles">{{ computedMiles }} miles</div>
          </div>
        </div>
      </div>

      <div class="mb-3">
        <label class="form-label">Reason for Change</label>
        <el-input
          v-model="changeReason"
          type="textarea"
          :rows="3"
          placeholder="Reason for Change"
          class="w-100"
        />
      </div>
    </div>

    <template slot="footer">
      <div class="text-center">
        <el-button @click="handleCancel">Cancel</el-button>
        <el-button
          type="primary"
          @click="handleUpdateClockTimes"
          :loading="updating"
          :disabled="
            isGroupShift
              ? !changeReason.trim()
              : !newClockInTime || !newClockOutTime || !changeReason.trim()
          "
        >
          <i class="fa-solid fa-save"></i> Update Shift
        </el-button>
      </div>
    </template>
  </AIVDialog>
</template>

<script>
import AIVDialog from "@/components/AIVDialog.vue";
import AIVTimeSelect from "@/components/AIVTimeSelect.vue";
import AIVSelect from "@/components/AIVSelect.vue";
import { shiftsService } from "@/serviceHandlers/shiftsService";
import { serviceCodesService } from "@/serviceHandlers/serviceCodesService";
import { nonServiceCodesService } from "@/serviceHandlers/nonServiceCodesService";
import { clientsService } from "@/serviceHandlers/clientsService";
import { shiftNoteQuestionsService } from "@/serviceHandlers/shiftNoteQuestionsService";
import { isDSPDGroupShift } from "@/utils/shiftStatus";
import { useLoginStore } from "src/stores/loginStore";

export default {
  name: "EditClockOutTimeModal",
  components: { AIVDialog, AIVTimeSelect, AIVSelect },

  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    shift: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      loginStore: useLoginStore(),
      updating: false,
      newClockInTime: null,
      newClockOutTime: null,
      breakStartTime: null,
      breakEndTime: null,
      changeReason: "",
      selectedPayer: null,
      selectedClientId: null,
      selectedServiceCodeId: null,
      payerOptions: ["VR", "DSPD", "Non Service"],
      availableClients: [],
      availableServiceCodes: [],
      loadingServiceCodes: false,
      loadingClients: false,
      initializingPayer: false,
      caregiverClockData: [],
      // Shadow/training caregivers' own clock rows (non-group shifts)
      sideClockData: [],
      clientTaskData: [],
      updatingClientTasks: false,
      serviceCodeQuestions: [],
      clientAnswers: {},
      clientGoals: {},
      expandedClients: {},
      loadingQA: false,
      // DSPD 1-on-1 shift Q&A
      dspdOneOnOneQuestions: [],
      dspdOneOnOneGoals: [],
      dspdOneOnOneQuestionAnswers: {},
      dspdOneOnOneGoalAnswers: {},
      dspdOneOnOneConcerns: "",
      dspdOneOnOneLoaded: false,
      // VR 1-on-1 shift Q&A (admin-configured shift-note questions)
      vrServiceCodeQuestions: [],
      vrQuestionAnswers: {},
      vrQuestionsLoaded: false,
      medicationLogs: {}, // Map of clientId to medication administration logs
      clientAddressOptions: {}, // Map of clientId to address options for dropdowns
      addingAddressFor: null, // { clientId, type: 'pickup'|'dropoff' }
      newAddress: { address1: "", address2: "", city: "", state: "", zip: "" },
      savingAddress: false,
      mileageStartLocation: "",
      mileageMidLocation: "",
      mileageEndLocation: "",
      odometerStart: null,
      odometerEnd: null,
    };
  },

  computed: {
    isVisible: {
      get() {
        return this.visible;
      },
      set(value) {
        this.$emit("update:visible", value);
      },
    },

    isGroupShift() {
      if (!this.shift) return false;
      return isDSPDGroupShift(this.shift) || isDSPDGroupShift(this.shift?.originalData);
    },

    shiftHasTransport() {
      const s = this.shift || {};
      const o = s.originalData || {};
      return !!(
        s.is_transporting ||
        o.is_transporting ||
        s.odometer_start != null ||
        o.odometer_start != null
      );
    },

    computedMiles() {
      const start = parseFloat(this.odometerStart);
      const end = parseFloat(this.odometerEnd);
      if (!isNaN(start) && !isNaN(end)) {
        const miles = end - start;
        return miles > 0 ? miles.toFixed(1) : null;
      }
      return null;
    },

    isDSPDOneOnOneShift() {
      if (!this.shift) return false;
      let payer = this.selectedPayer || this.shift.payer || this.shift.originalData?.payer;
      if (!payer) {
        const clients = this.shift.clients || this.shift.originalData?.clients || [];
        if (clients.length > 0 && clients[0].payer_type) {
          payer = clients[0].payer_type;
        }
      }
      return payer === "DSPD" && !this.isGroupShift;
    },

    // Legacy DSPD 1-on-1 concerns already stored as shifts.shift_notes
    hasLegacyConcerns() {
      const s = this.shift || {};
      const o = s.originalData || {};
      return !!((s.shift_notes || o.shift_notes || "").toString().trim());
    },

    // Hardcoded concerns textarea: legacy shifts and question-less codes only
    showConcernsField() {
      return (
        this.isDSPDOneOnOneShift &&
        (this.hasLegacyConcerns || this.dspdOneOnOneQuestions.length === 0)
      );
    },

    // Rendered question list — hides the seeded concerns question while the
    // legacy concerns textarea is active.
    visibleDspdQuestions() {
      if (!this.showConcernsField) return this.dspdOneOnOneQuestions;
      return this.dspdOneOnOneQuestions.filter(
        (q) => q.question_key !== "dspd_concerns",
      );
    },

    isVRShift() {
      if (!this.shift) return false;
      let payer = this.selectedPayer || this.shift.payer || this.shift.originalData?.payer;
      if (!payer) {
        const clients = this.shift.clients || this.shift.originalData?.clients || [];
        if (clients.length > 0 && clients[0].payer_type) {
          payer = clients[0].payer_type;
        }
      }
      return payer === "VR" && !this.isGroupShift;
    },

    caregiverOptions() {
      if (!this.shift?.caregivers) return [];
      const clockTimes = this.shift.caregiverClockTimes || this.shift.originalData?.caregiverClockTimes || [];
      return this.shift.caregivers.map((c) => {
        let name = `${c.first_name || ""} ${c.last_name || ""}`.trim();
        if (!name) {
          const ct = clockTimes.find((ct) => Number(ct.id_users) === Number(c.id));
          if (ct?.caregiver) {
            name = `${ct.caregiver.first_name || ""} ${ct.caregiver.last_name || ""}`.trim();
          }
        }
        return { id: c.id, name: name || "Unknown" };
      });
    },

    shouldShowBreakFields() {
      if (!this.shift) return false;

      // Check if the shift already has break times recorded
      const hasExistingBreaks =
        this.shift.originalData?.break_start ||
        this.shift.break_start ||
        this.shift.originalData?.break_end ||
        this.shift.break_end ||
        (this.shift.breaks && this.shift.breaks.length > 0);

      // If shift already has breaks, allow editing them regardless of payer
      if (hasExistingBreaks) return true;

      // For JC shifts >= 4 hours, allow adding new breaks
      const payer = this.selectedPayer || this.shift.payer || this.shift.originalData?.payer || this.shift.payer_type;
      const isJC = payer === 'JC';
      if (!isJC) return false;

      // Only for shifts >= 4 hours
      const scheduledStart = this.shift.scheduledStartTime || this.shift.originalData?.scheduled_start || this.shift.startTime || this.shift.scheduled_start;
      const scheduledEnd = this.shift.scheduledEndTime || this.shift.originalData?.scheduled_end || this.shift.endTime || this.shift.scheduled_end;
      const dateStr = this.shift.date?.split('T')[0];

      if (!scheduledStart || !scheduledEnd || !dateStr) return false;

      const startDate = new Date(`${dateStr}T${scheduledStart}`);
      const endDate = new Date(`${dateStr}T${scheduledEnd}`);
      if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) return false;

      const durationHours = (endDate - startDate) / (1000 * 60 * 60);
      return durationHours >= 4;
    },

    breakDurationMinutes() {
      if (!this.breakStartTime || !this.breakEndTime) return null;

      try {
        const start = new Date(`2000-01-01T${this.breakStartTime}`);
        const end = new Date(`2000-01-01T${this.breakEndTime}`);

        if (isNaN(start.getTime()) || isNaN(end.getTime())) return null;

        const durationMs = end - start;
        if (durationMs < 0) return null; // Break end must be after break start

        return Math.round(durationMs / (1000 * 60));
      } catch (error) {
        return null;
      }
    },

    breakDurationFormatted() {
      if (!this.breakDurationMinutes) return '';

      const hours = Math.floor(this.breakDurationMinutes / 60);
      const minutes = this.breakDurationMinutes % 60;

      if (hours > 0) {
        return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
      }
      return `${minutes}m`;
    },
  },

  watch: {
    visible(newVal) {
      if (newVal) {
        this.resetForm();
        this.initializeTimes();
        this.initializePayerAndServiceCode();
        // Load DSPD 1-on-1 Q&A data when the modal opens
        // (the isDSPDOneOnOneShift watcher alone is insufficient because
        // the value may already be true before the modal becomes visible)
        if (this.isDSPDOneOnOneShift) {
          this.loadDSPDOneOnOneData();
        }
        if (this.isVRShift) {
          this.loadVRQuestionData();
        }
      }
    },
    isDSPDOneOnOneShift(newVal) {
      if (newVal && this.visible) {
        this.loadDSPDOneOnOneData();
      }
    },
    isVRShift(newVal) {
      if (newVal && this.visible) {
        this.loadVRQuestionData();
      }
    },
    async selectedPayer(newPayer, oldPayer) {
      // Skip if we're in the middle of initialization
      if (this.initializingPayer) return;

      if (newPayer === "VR") {
        // Load clients when VR is selected
        await this.loadClients();
      } else {
        // Clear client selection when not VR
        this.selectedClientId = null;
        this.availableClients = [];
      }

      if (newPayer) {
        // Clear the selected service code when payer changes
        // since the available codes will be different
        if (oldPayer !== newPayer) {
          this.selectedServiceCodeId = null;
        }
        this.loadAvailableServiceCodes();
      }
    },
    selectedClientId(newClientId) {
      // Reload service codes when client changes
      if ((this.selectedPayer === "VR" || this.selectedPayer === "DSPD") && newClientId) {
        this.loadAvailableServiceCodes();
      }
    },
  },

  methods: {
    resetForm() {
      this.newClockInTime = null;
      this.newClockOutTime = null;
      this.breakStartTime = null;
      this.breakEndTime = null;
      this.changeReason = "";
      this.selectedPayer = null;
      this.selectedClientId = null;
      this.selectedServiceCodeId = null;
      this.availableClients = [];
      this.availableServiceCodes = [];
      this.caregiverClockData = [];
      this.clientTaskData = [];
      this.serviceCodeQuestions = [];
      this.clientAnswers = {};
      this.clientGoals = {};
      this.expandedClients = {};
      this.dspdOneOnOneQuestions = [];
      this.dspdOneOnOneGoals = [];
      this.dspdOneOnOneQuestionAnswers = {};
      this.dspdOneOnOneGoalAnswers = {};
      this.dspdOneOnOneConcerns = "";
      this.dspdOneOnOneLoaded = false;
      this.vrServiceCodeQuestions = [];
      this.vrQuestionAnswers = {};
      this.vrQuestionsLoaded = false;
      this.medicationLogs = {};
      this.clientAddressOptions = {};
      this.addingAddressFor = null;
      this.newAddress = { address1: "", address2: "", city: "", state: "", zip: "" };
      this.savingAddress = false;
      this.mileageStartLocation = "";
      this.mileageMidLocation = "";
      this.mileageEndLocation = "";
      this.odometerStart = null;
      this.odometerEnd = null;
    },

    initializeTimes() {
      if (!this.shift) return;

      this.initializeMileageData();

      // For DSPD group shifts, initialize per-caregiver clock times
      if (this.isGroupShift) {
        this.initializeCaregiverClockData();
        this.initializeClientTaskData();
        return;
      }

      // Non-group: shadow/training caregivers keep their own clock rows
      this.initializeSideClockData();

      // Set default clock in time if available
      const clockInTime =
        this.shift.originalData?.clockIn ||
        this.shift.originalData?.clock_in ||
        this.shift.actualStartTime;
      if (clockInTime) {
        const localClockIn = this.convertUTCDateTimeToLocalTime(clockInTime);
        this.newClockInTime = localClockIn;
      } else {
        // Default to scheduled start time
        const startTime = this.shift.startTime;
        if (startTime) {
          this.newClockInTime = this.convertUTCTimeToLocalTime(startTime);
        }
      }

      // Set default clock out time if available
      const clockOutTime =
        this.shift.originalData?.clockOut ||
        this.shift.originalData?.clock_out ||
        this.shift.actualEndTime;
      if (clockOutTime) {
        const localClockOut = this.convertUTCDateTimeToLocalTime(clockOutTime);
        this.newClockOutTime = localClockOut;
      } else {
        // Default to scheduled end time
        const endTime = this.shift.endTime;
        if (endTime) {
          this.newClockOutTime = this.convertUTCTimeToLocalTime(endTime);
        }
      }

      // Initialize break times if available (for shifts with breaks)
      if (this.shouldShowBreakFields) {
        // Check for break times in multiple locations
        let breakStart = this.shift.originalData?.break_start || this.shift.break_start;
        let breakEnd = this.shift.originalData?.break_end || this.shift.break_end;

        // If not found as direct properties, check the breaks array
        if (!breakStart && !breakEnd && this.shift.breaks && this.shift.breaks.length > 0) {
          // Get the most recent break (last in array, or we can get the first one)
          const latestBreak = this.shift.breaks[this.shift.breaks.length - 1];
          breakStart = latestBreak.break_start;
          breakEnd = latestBreak.break_end;
        }

        if (breakStart) {
          const localBreakStart = this.convertUTCDateTimeToLocalTime(breakStart);
          this.breakStartTime = localBreakStart;
        }

        if (breakEnd) {
          const localBreakEnd = this.convertUTCDateTimeToLocalTime(breakEnd);
          this.breakEndTime = localBreakEnd;
        }
      }
    },

    initializeMileageData() {
      const s = this.shift || {};
      const o = s.originalData || {};
      this.mileageStartLocation = s.mileage_start_location ?? o.mileage_start_location ?? "";
      this.mileageMidLocation = s.mileage_mid_location ?? o.mileage_mid_location ?? "";
      this.mileageEndLocation = s.mileage_end_location ?? o.mileage_end_location ?? "";
      const oStart = s.odometer_start ?? o.odometer_start;
      const oEnd = s.odometer_end ?? o.odometer_end;
      this.odometerStart = oStart != null ? oStart : null;
      this.odometerEnd = oEnd != null ? oEnd : null;
    },

    initializeSideClockData() {
      this.sideClockData = [];
      const caregivers =
        this.shift.caregivers || this.shift.originalData?.caregivers || [];
      const clockTimes =
        this.shift.caregiverClockTimes ||
        this.shift.originalData?.caregiverClockTimes ||
        [];
      this.sideClockData = caregivers
        .filter((c) => (c.role || "primary") !== "primary")
        .map((c) => {
          const ct = clockTimes.find(
            (x) => Number(x.id_users) === Number(c.id),
          );
          const name =
            c.name || `${c.first_name || ""} ${c.last_name || ""}`.trim();
          const roleLabel =
            c.role.charAt(0).toUpperCase() + c.role.slice(1);
          return {
            caregiverId: c.id,
            caregiverName: `${name} (${roleLabel})`,
            clockIn: ct?.clock_in
              ? this.convertUTCDateTimeToLocalTime(ct.clock_in)
              : null,
            clockOut: ct?.clock_out
              ? this.convertUTCDateTimeToLocalTime(ct.clock_out)
              : null,
          };
        });
    },

    initializeCaregiverClockData() {
      const caregivers = this.shift.caregivers || [];
      const clockTimes = this.shift.caregiverClockTimes || this.shift.originalData?.caregiverClockTimes || [];

      this.caregiverClockData = caregivers.map((caregiver) => {
        const ct = clockTimes.find(
          (c) => Number(c.id_users) === Number(caregiver.id),
        );

        // Try caregiver object first, then fall back to clock time's nested caregiver
        let name = `${caregiver.first_name || ""} ${caregiver.last_name || ""}`.trim();
        if (!name && ct?.caregiver) {
          name = `${ct.caregiver.first_name || ""} ${ct.caregiver.last_name || ""}`.trim();
        }

        return {
          caregiverId: caregiver.id,
          caregiverName: name || "Unknown",
          clockIn: ct?.clock_in ? this.convertUTCDateTimeToLocalTime(ct.clock_in) : null,
          clockOut: ct?.clock_out ? this.convertUTCDateTimeToLocalTime(ct.clock_out) : null,
        };
      });
    },

    initializeClientTaskData() {
      const clientDetails = this.shift.clientDetails || this.shift.originalData?.clientDetails || [];
      const shiftClients = this.shift.clients || this.shift.originalData?.clients || [];

      // Build task data from clientDetails entries
      const taskData = clientDetails.map((cd) => {
        // Try nested client object first, then fall back to shift.clients by id
        let clientName = "Unknown Client";
        if (cd.client) {
          clientName = `${cd.client.first_name || ""} ${cd.client.last_name || ""}`.trim() || "Unknown Client";
        } else if (cd.id_clients) {
          const client = shiftClients.find((c) => Number(c.id) === Number(cd.id_clients));
          if (client) {
            clientName = `${client.first_name || ""} ${client.last_name || ""}`.trim() || "Unknown Client";
          }
        }

        return {
          id: cd.id,
          clientName,
          clientId: cd.id_clients || cd.client_id || cd.client?.id,
          pickupCaregiverId: cd.pickup_caregiver_id,
          pickupTime: cd.pickup_time,
          pickupTimeLocal: cd.pickup_time ? this.convertUTCDateTimeToLocalTime(cd.pickup_time) : null,
          pickupAddressId: cd.pickup_address_id || null,
          dropoffCaregiverId: cd.dropoff_caregiver_id,
          dropoffTime: cd.dropoff_time,
          dropoffTimeLocal: cd.dropoff_time ? this.convertUTCDateTimeToLocalTime(cd.dropoff_time) : null,
          dropoffAddressId: cd.dropoff_address_id || null,
          shiftNotesCaregiverId: cd.shift_notes_caregiver_id,
          shiftNotesCompleted: cd.shift_notes_completed,
          medicationsCaregiverId: cd.medications_caregiver_id,
          medicationsCompleted: cd.medications_completed,
          didNotAttend: cd.did_not_attend || false,
        };
      });

      // Add entries for any clients that don't have a clientDetails row yet
      const coveredClientIds = new Set(taskData.map((t) => Number(t.clientId)).filter(Boolean));
      shiftClients.forEach((client) => {
        if (!coveredClientIds.has(Number(client.id))) {
          const clientName = `${client.first_name || ""} ${client.last_name || ""}`.trim() || "Unknown Client";
          taskData.push({
            id: `new-${client.id}`,
            clientName,
            clientId: client.id,
            pickupCaregiverId: null,
            pickupTime: null,
            pickupTimeLocal: null,
            pickupAddressId: null,
            dropoffCaregiverId: null,
            dropoffTime: null,
            dropoffTimeLocal: null,
            dropoffAddressId: null,
            shiftNotesCaregiverId: null,
            shiftNotesCompleted: false,
            medicationsCaregiverId: null,
            medicationsCompleted: null,
          });
        }
      });

      this.clientTaskData = taskData;

      // Load client addresses for pickup/dropoff location editing
      this.loadClientAddresses();

      // Load Q&A data and medication logs
      this.loadQAData();
      this.fetchMedicationLogs();
    },

    async initializePayerAndServiceCode() {
      if (!this.shift) return;

      // Set flag to prevent watcher from triggering during initialization
      this.initializingPayer = true;

      try {
        // Set current payer - for DSPD group shifts, force DSPD
        if (this.isGroupShift) {
          this.selectedPayer = "DSPD";
        } else {
          this.selectedPayer =
            this.shift.payer || this.shift.originalData?.payer || "VR";
        }

        // Load clients and set current client for VR and DSPD payers
        if (this.selectedPayer === "VR" || this.selectedPayer === "DSPD") {
          if (this.selectedPayer === "VR") {
            await this.loadClients();
          }
          const clientId = this.getClientId();
          if (clientId) {
            this.selectedClientId = clientId;
          }
        }

        // Load service codes based on payer
        await this.loadAvailableServiceCodes();

        // Set current service code if available
        // Check the appropriate code array based on payer type
        const shiftServiceCodes =
          this.selectedPayer === "Non Service"
            ? this.shift.non_service_codes ||
              this.shift.originalData?.non_service_codes ||
              []
            : this.shift.service_codes ||
              this.shift.originalData?.service_codes ||
              [];

        if (shiftServiceCodes.length > 0) {
          // Only set if the code exists in the available codes
          const codeExists = this.availableServiceCodes.some(
            (code) => code.id === shiftServiceCodes[0].id,
          );
          if (codeExists) {
            this.selectedServiceCodeId = shiftServiceCodes[0].id;
          } else {
            // If the current code is not in available codes, it might be because
            // the payer was changed or the code type doesn't match
            // Try to find if it's in the other type (service vs non-service)
            // For now, just auto-select if there's only one option
            if (this.availableServiceCodes.length === 1) {
              this.selectedServiceCodeId = this.availableServiceCodes[0].id;
            }
          }
        } else if (this.availableServiceCodes.length === 1) {
          // Auto-select if only one option and no current selection
          this.selectedServiceCodeId = this.availableServiceCodes[0].id;
        }
      } finally {
        // Always clear the flag
        this.initializingPayer = false;
      }
    },

    async loadClients() {
      this.loadingClients = true;
      try {
        const clients = await clientsService.list({ slim: true });
        this.availableClients = (clients || []).map((client) => ({
          id: Number(client.id) || client.id,
          name: `${client.first_name} ${client.last_name}`,
        }));
      } catch (error) {
        this.availableClients = [];
        this.$notifyError("Failed to load clients");
      } finally {
        this.loadingClients = false;
      }
    },

    handleClientChange(clientId) {
      this.selectedClientId = clientId;
      // Clear service code selection when client changes
      this.selectedServiceCodeId = null;
    },

    getClientId() {
      // Helper method to get client ID from the shift object
      if (!this.shift) return null;

      // Check various possible locations for client ID
      return (
        this.shift.clientId ||
        this.shift.client_id ||
        this.shift.originalData?.clientId ||
        this.shift.originalData?.client_id ||
        (this.shift.clients && this.shift.clients.length > 0
          ? this.shift.clients[0].id
          : null)
      );
    },

    async loadAvailableServiceCodes() {
      if (!this.selectedPayer) return;

      this.loadingServiceCodes = true;
      try {
        if (this.selectedPayer === "Non Service") {
          const codes = await nonServiceCodesService.list();
          this.availableServiceCodes = codes || [];
        } else if (this.selectedPayer === "VR") {
          // For VR payer, fetch service codes by client
          if (this.selectedClientId) {
            const codes = await serviceCodesService.getByClient({
              clientId: this.selectedClientId,
              payer_type: "VR",
            });
            this.availableServiceCodes = codes || [];
          } else {
            // No client selected, don't load service codes yet
            this.availableServiceCodes = [];
          }
        } else if (this.selectedPayer === "DSPD") {
          // For DSPD payer, fetch service codes by client auth
          if (this.selectedClientId) {
            const codes = await serviceCodesService.getByClient({
              clientId: this.selectedClientId,
              payer_type: "DSPD",
            });
            let filtered = codes || [];
            // For 1-on-1 shifts, exclude group codes (DSP, DSG)
            if (!this.isGroupShift) {
              filtered = filtered.filter(sc => sc.code !== 'DSP' && sc.code !== 'DSG');
            }
            this.availableServiceCodes = filtered;
          } else {
            this.availableServiceCodes = [];
          }
        } else {
          // For other payers (Private), load all service codes
          const codes = await serviceCodesService.list();
          this.availableServiceCodes = codes || [];
        }

        // Auto-select if only one option
        if (this.availableServiceCodes.length === 1) {
          this.selectedServiceCodeId = this.availableServiceCodes[0].id;
        }
      } catch (error) {
        this.availableServiceCodes = [];
        this.$notifyError("Failed to load service codes");
      } finally {
        this.loadingServiceCodes = false;
      }
    },

    convertUTCDateTimeToLocalTime(utcDateTimeStr) {
      if (!utcDateTimeStr) return "";

      // Parse the datetime string - it should already be a proper UTC timestamp from backend
      const utcDate = new Date(utcDateTimeStr);
      if (isNaN(utcDate.getTime())) return "";

      // Convert to local time string (HH:MM format)
      return utcDate.toLocaleTimeString("en-US", {
        hour12: false,
        hour: "2-digit",
        minute: "2-digit",
      });
    },

    handleCancel() {
      this.isVisible = false;
      this.resetForm();
    },

    async handleUpdateClockTimes() {
      // For group shifts, validate per-caregiver clock data
      if (this.isGroupShift) {
        if (!this.changeReason.trim()) {
          this.$notifyError("Please provide a reason for the change.");
          return;
        }
        if (!this.selectedServiceCodeId) {
          this.$notifyError("Please select a service code.");
          return;
        }

        // Validate each caregiver's clock times
        for (const cg of this.caregiverClockData) {
          if (cg.clockIn && cg.clockOut) {
            const [inH, inM] = cg.clockIn.split(":").map(Number);
            const [outH, outM] = cg.clockOut.split(":").map(Number);
            if (outH * 60 + outM <= inH * 60 + inM) {
              this.$notifyError(
                `Clock out time must be after clock in time for ${cg.caregiverName}.`,
              );
              return;
            }
          }
        }

        await this.handleGroupShiftUpdate();
        return;
      }

      if (
        !this.newClockInTime ||
        !this.newClockOutTime ||
        !this.changeReason.trim()
      ) {
        this.$notifyError(
          "Please select both clock in and clock out times and provide a reason for the change.",
        );
        return;
      }

      if (!this.selectedPayer) {
        this.$notifyError("Please select a payer.");
        return;
      }

      if (!this.selectedServiceCodeId) {
        this.$notifyError("Please select a service code.");
        return;
      }

      if (!this.shift || !this.shift.date) {
        this.$notifyError("Shift data is not available. Please try again.");
        return;
      }

      // Validate that clock out time is after clock in time
      const [inHours, inMinutes] = this.newClockInTime.split(":").map(Number);
      const [outHours, outMinutes] = this.newClockOutTime
        .split(":")
        .map(Number);

      const clockInMinutes = inHours * 60 + inMinutes;
      const clockOutMinutes = outHours * 60 + outMinutes;

      if (clockOutMinutes <= clockInMinutes) {
        this.$notifyError("Clock out time must be after clock in time.");
        return;
      }

      // Validate required VR service-code questions
      if (this.isVRShift && this.vrServiceCodeQuestions.length) {
        for (const q of this.vrServiceCodeQuestions) {
          if (q.required) {
            const ans = this.vrQuestionAnswers[q.id];
            if (!ans || !ans.toString().trim()) {
              this.$notifyError(`Please answer: "${q.question_text}"`);
              return;
            }
          }
        }
      }

      this.updating = true;
      try {
        // Extract the date portion from shift.date to avoid timezone issues
        let dateStr = this.shift.date;
        if (dateStr.includes("T")) {
          dateStr = dateStr.split("T")[0]; // Get just YYYY-MM-DD
        }
        const [year, month, day] = dateStr.split("-").map(Number);

        // Create clock in datetime using explicit date components
        // Month is 0-indexed in Date constructor
        const localClockIn = new Date(
          year,
          month - 1,
          day,
          inHours,
          inMinutes,
          0,
          0,
        );

        // Create clock out datetime
        const localClockOut = new Date(
          year,
          month - 1,
          day,
          outHours,
          outMinutes,
          0,
          0,
        );

        const payload = {
          clock_in: localClockIn.toISOString(),
          clock_out: localClockOut.toISOString(),
          payer: this.selectedPayer,
          clientId: this.selectedClientId,
          serviceCodeIds: [this.selectedServiceCodeId],
          adminChangeReason: this.changeReason.trim(),
        };

        // Shadow/training caregivers' own clock rows (side rows) —
        // upserted per caregiver by the API, independent of the
        // shift-level clock above
        const sideRows = this.sideClockData
          .filter((cg) => cg.clockIn && cg.clockOut)
          .map((cg) => {
            const [sInH, sInM] = cg.clockIn.split(":").map(Number);
            const [sOutH, sOutM] = cg.clockOut.split(":").map(Number);
            return {
              id_users: cg.caregiverId,
              clock_in: new Date(year, month - 1, day, sInH, sInM, 0, 0).toISOString(),
              clock_out: new Date(year, month - 1, day, sOutH, sOutM, 0, 0).toISOString(),
            };
          });
        if (sideRows.length > 0) {
          payload.caregiverClockTimes = sideRows;
        }

        // Add DSPD 1-on-1 Q&A data if applicable
        if (this.isDSPDOneOnOneShift) {
          if (this.showConcernsField) {
            payload.shift_notes = this.dspdOneOnOneConcerns.trim();
          }
          payload.questionAnswers = Object.entries(this.dspdOneOnOneQuestionAnswers)
            .filter(([, answer]) => answer != null && answer.toString().trim())
            .map(([questionId, answer]) => ({
              questionId: Number(questionId),
              answer: answer.toString().trim(),
            }));
          payload.goalAnswers = Object.entries(this.dspdOneOnOneGoalAnswers)
            .filter(([, answer]) => answer != null && answer.toString().trim())
            .map(([goalId, answer]) => ({
              goalId: Number(goalId),
              answer: answer.toString().trim(),
            }));
        }

        // Add VR service-code question answers. Only when questions are
        // configured, so VR shifts without questions keep existing behavior.
        if (this.isVRShift && this.vrServiceCodeQuestions.length) {
          payload.questionAnswers = Object.entries(this.vrQuestionAnswers)
            .filter(([, answer]) => answer != null && answer.toString().trim())
            .map(([questionId, answer]) => ({
              questionId: Number(questionId),
              answer: answer.toString().trim(),
            }));
        }

        // Add break times if applicable (for shifts with breaks)
        if (this.shouldShowBreakFields) {
          if (this.breakStartTime) {
            const [breakStartHours, breakStartMinutes] = this.breakStartTime.split(":").map(Number);
            const localBreakStart = new Date(
              year,
              month - 1,
              day,
              breakStartHours,
              breakStartMinutes,
              0,
              0,
            );
            payload.break_start = localBreakStart.toISOString();
          } else {
            payload.break_start = null;
          }

          if (this.breakEndTime) {
            const [breakEndHours, breakEndMinutes] = this.breakEndTime.split(":").map(Number);
            const localBreakEnd = new Date(
              year,
              month - 1,
              day,
              breakEndHours,
              breakEndMinutes,
              0,
              0,
            );
            payload.break_end = localBreakEnd.toISOString();
          } else {
            payload.break_end = null;
          }
        }

        if (this.shiftHasTransport) {
          payload.mileage_start_location = this.mileageStartLocation || null;
          payload.mileage_mid_location = this.mileageMidLocation || null;
          payload.mileage_end_location = this.mileageEndLocation || null;
          payload.odometer_start =
            this.odometerStart !== null && this.odometerStart !== ""
              ? parseFloat(this.odometerStart)
              : null;
          payload.odometer_end =
            this.odometerEnd !== null && this.odometerEnd !== ""
              ? parseFloat(this.odometerEnd)
              : null;
        }

        await shiftsService.update(
          this.shift.originalData?.id || this.shift.id,
          payload,
        );

        this.$notifySuccess("Shift updated successfully!");
        this.isVisible = false;
        this.resetForm();
        this.$emit("clock-times-updated", {
          shiftId: this.shift.id,
          newClockInTime: this.newClockInTime,
          newClockOutTime: this.newClockOutTime,
        });
      } catch (error) {
        this.$notifyError(
          "Error updating clock times: " +
            (error.response?.data?.error ||
              error.response?.data?.message ||
              error.message),
        );
      } finally {
        this.updating = false;
      }
    },

    async handleGroupShiftUpdate() {
      this.updating = true;
      try {
        const shiftId = this.shift.originalData?.id || this.shift.id;

        let dateStr = this.shift.date;
        if (dateStr.includes("T")) {
          dateStr = dateStr.split("T")[0];
        }
        const [year, month, day] = dateStr.split("-").map(Number);

        // Build per-caregiver clock times payload
        const caregiverClockTimes = this.caregiverClockData
          .filter((cg) => cg.clockIn && cg.clockOut)
          .map((cg) => {
            const [inH, inM] = cg.clockIn.split(":").map(Number);
            const [outH, outM] = cg.clockOut.split(":").map(Number);

            const localIn = new Date(year, month - 1, day, inH, inM, 0, 0);
            const localOut = new Date(year, month - 1, day, outH, outM, 0, 0);

            return {
              id_users: cg.caregiverId,
              clock_in: localIn.toISOString(),
              clock_out: localOut.toISOString(),
            };
          });

        // Build client details payload
        const clientDetailsPayload = this.clientTaskData.map((task) => {
          // Convert local time strings back to ISO datetimes
          let pickupTimeISO = null;
          if (task.pickupTimeLocal) {
            const [pH, pM] = task.pickupTimeLocal.split(":").map(Number);
            pickupTimeISO = new Date(year, month - 1, day, pH, pM, 0, 0).toISOString();
          }

          let dropoffTimeISO = null;
          if (task.dropoffTimeLocal) {
            const [dH, dM] = task.dropoffTimeLocal.split(":").map(Number);
            dropoffTimeISO = new Date(year, month - 1, day, dH, dM, 0, 0).toISOString();
          }

          const answers = this.clientAnswers[task.clientId] || {};
          const notesComplete = task.shiftNotesCompleted || this.isClientNotesComplete(task.clientId);
          const notesCaregiverId = task.shiftNotesCaregiverId || (notesComplete ? this.caregiverClockData[0]?.caregiverId : null);

          return {
            clientDetailId: task.id,
            clientId: task.clientId,
            pickupCaregiverId: task.pickupCaregiverId,
            pickupTime: pickupTimeISO,
            pickupAddressId: task.pickupAddressId || null,
            dropoffCaregiverId: task.dropoffCaregiverId,
            dropoffTime: dropoffTimeISO,
            dropoffAddressId: task.dropoffAddressId || null,
            shiftNotesCaregiverId: notesCaregiverId,
            shiftNotesCompleted: notesComplete,
            medicationsCaregiverId: task.medicationsCaregiverId,
            shiftNotes: answers.shiftNotes || "",
            questionAnswers: Object.entries(answers.questionAnswers || {})
              .filter(([, answer]) => answer != null && answer.toString().trim())
              .map(([questionId, answer]) => ({
                questionId: Number(questionId),
                answer: answer.toString().trim(),
              })),
            goalAnswers: Object.entries(answers.goalAnswers || {})
              .filter(([, answer]) => answer != null && answer.toString().trim())
              .map(([goalId, answer]) => ({
                goalId: Number(goalId),
                answer: answer.toString().trim(),
              })),
          };
        });

        // Single API call for all group shift updates (creates one history entry)
        const payload = {
          payer: this.selectedPayer,
          serviceCodeIds: [this.selectedServiceCodeId],
          caregiverClockTimes,
          clientDetails: clientDetailsPayload,
          adminChangeReason: this.changeReason.trim(),
        };

        if (this.shiftHasTransport) {
          payload.mileageStartLocation = this.mileageStartLocation || null;
          payload.mileageMidLocation = this.mileageMidLocation || null;
          payload.mileageEndLocation = this.mileageEndLocation || null;
          payload.odometerStart =
            this.odometerStart !== null && this.odometerStart !== ""
              ? parseFloat(this.odometerStart)
              : null;
          payload.odometerEnd =
            this.odometerEnd !== null && this.odometerEnd !== ""
              ? parseFloat(this.odometerEnd)
              : null;
        }

        await shiftsService.adminUpdateClientDetails(shiftId, payload);

        this.$notifySuccess("Group shift updated successfully!");
        this.isVisible = false;
        this.resetForm();
        this.$emit("clock-times-updated", { shiftId: this.shift.id });
      } catch (error) {
        this.$notifyError(
          "Error updating group shift: " +
            (error.response?.data?.error ||
              error.response?.data?.message ||
              error.message),
        );
      } finally {
        this.updating = false;
      }
    },

    formatClockTime(dateTimeString) {
      if (!dateTimeString) return "";

      // Use the same conversion logic to get local time
      const localTime = this.convertUTCDateTimeToLocalTime(dateTimeString);

      // Convert HH:MM to 12-hour format for display
      if (!localTime) return "";

      const [hours, minutes] = localTime.split(":").map(Number);
      const ampm = hours >= 12 ? "PM" : "AM";
      const displayHours = hours % 12 || 12;

      return `${displayHours}:${minutes.toString().padStart(2, "0")} ${ampm}`;
    },

    formatTime(timeString) {
      if (!timeString) return "";

      const localTimeString = this.convertUTCTimeToLocalTime(timeString);
      if (!localTimeString) return "";

      const timeParts = localTimeString.split(":");
      let hours = parseInt(timeParts[0]);
      const minutes = timeParts[1] || "00";

      const ampm = hours >= 12 ? "pm" : "am";
      hours = hours % 12;
      hours = hours ? hours : 12;

      const formattedMinutes = minutes === "00" ? "00" : minutes;
      return `${hours}:${formattedMinutes}${ampm}`;
    },

    convertUTCTimeToLocalTime(timeStr) {
      if (!timeStr || !this.shift?.date) return timeStr;

      try {
        const shiftDate = this.shift.date.split("T")[0];
        const utcDateTime = `${shiftDate}T${timeStr}Z`;
        const utcDate = new Date(utcDateTime);

        if (isNaN(utcDate.getTime())) {
          return timeStr;
        }

        return utcDate.toLocaleTimeString("en-US", {
          hour12: false,
          hour: "2-digit",
          minute: "2-digit",
        });
      } catch (error) {
        return timeStr;
      }
    },

    formatDate(dateString) {
      if (!dateString) return "";
      // Handle date string to avoid timezone issues
      let dateStr = dateString;
      if (dateStr.includes("T")) {
        dateStr = dateStr.split("T")[0]; // Extract just the date part
      }

      // Create date object with explicit local timezone to avoid UTC interpretation
      const [year, month, day] = dateStr.split("-");
      const dateObj = new Date(year, month - 1, day); // month is 0-indexed

      return dateObj.toLocaleDateString("en-US", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    },

    formatScheduledTime(timeStr, date = null) {
      if (!timeStr) return "N/A";

      // If timeStr is already a full datetime (ISO string), use it directly
      if (timeStr.includes("T")) {
        const utcDate = new Date(timeStr);
        if (!isNaN(utcDate.getTime())) {
          return utcDate.toLocaleTimeString("en-US", {
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
          });
        }
      }

      // Legacy: If we have a date, combine it with the time and treat as UTC
      if (date) {
        const dateOnly = date.split("T")[0];
        const utcDateTime = `${dateOnly}T${timeStr}Z`;
        const utcDate = new Date(utcDateTime);

        if (!isNaN(utcDate.getTime())) {
          return utcDate.toLocaleTimeString("en-US", {
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
          });
        }
      }

      // Fallback: treat as local time
      return this.formatTime12Hour(timeStr);
    },

    formatTime12Hour(timeStr) {
      if (!timeStr) return "";
      const [hour, minute] = timeStr.split(":").map(Number);
      const period = hour >= 12 ? "PM" : "AM";
      const displayHour = hour % 12 || 12;
      return `${displayHour}:${minute.toString().padStart(2, "0")}${period}`;
    },

    formatTimeRange(startTime, endTime, date = null) {
      // Handle new UTC datetime format (with 'T' in the string)
      if (startTime && startTime.includes("T")) {
        const start = this.formatScheduledTime(startTime);
        const end = this.formatScheduledTime(endTime);
        return `${start} - ${end}`;
      }

      // Legacy: handle old format with separate date and time
      if (date && startTime && endTime) {
        const start = this.formatScheduledTime(startTime, date);
        const end = this.formatScheduledTime(endTime, date);
        return `${start} - ${end}`;
      }

      // Fallback: format as simple time strings
      const start = this.formatTime12Hour(startTime);
      const end = this.formatTime12Hour(endTime);
      return `${start} - ${end}`;
    },

    formatDateTime(dateTimeString) {
      if (!dateTimeString) return "";

      // Use the same conversion logic
      const localDate = this.convertUTCDateTimeToLocal(dateTimeString);

      return localDate.toLocaleString("en-US", {
        weekday: "short",
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
    },

    formatStatus(status) {
      if (!status) return "";
      if (status.toLowerCase() === "error") return "Error";
      return status.charAt(0).toUpperCase() + status.slice(1).replace("-", " ");
    },

    formatClient(shift) {
      if (!shift) return "";
      if (shift.clients && shift.clients.length > 0) {
        return shift.clients[0].name;
      }
      return shift.client || "Unassigned";
    },

    formatCaregiver(shift) {
      if (!shift) return "";
      if (shift.caregivers && shift.caregivers.length > 0) {
        return shift.caregivers[0].name;
      }
      return shift.caregiver || "Unassigned";
    },

    getStatusClass(status) {
      if (!status) return "badge-secondary";

      const normalizedStatus = status.toLowerCase().replace(/\s+/g, "-");

      switch (normalizedStatus) {
        case "unassigned":
          return "badge-secondary";
        case "scheduled":
          return "badge-info";
        case "clocked-in":
        case "clocked in":
          return "badge-success";
        case "clocked-out":
        case "clocked out":
          return "badge-warning";
        case "approved":
          return "badge-primary";
        case "available":
          return "badge-success";
        case "missed":
          return "badge-secondary";
        case "error":
          return "badge-danger";
        default:
          return "badge-info";
      }
    },

    getTaskCaregiverName(caregiverId) {
      if (!caregiverId) return "Unknown";

      // Try shift.caregivers first
      if (this.shift?.caregivers) {
        const caregiver = this.shift.caregivers.find(
          (c) => Number(c.id) === Number(caregiverId),
        );
        if (caregiver) {
          const name = `${caregiver.first_name || ""} ${caregiver.last_name || ""}`.trim();
          if (name) return name;
        }
      }

      // Fall back to caregiverClockTimes nested caregiver
      const clockTimes = this.shift?.caregiverClockTimes || this.shift?.originalData?.caregiverClockTimes || [];
      const ct = clockTimes.find((c) => Number(c.id_users) === Number(caregiverId));
      if (ct?.caregiver) {
        const name = `${ct.caregiver.first_name || ""} ${ct.caregiver.last_name || ""}`.trim();
        if (name) return name;
      }

      return "Unknown";
    },

    formatAddressOption(addr) {
      if (!addr) return "";
      const parts = [addr.address1, addr.city, addr.state, addr.zip];
      return parts.filter(Boolean).join(", ");
    },

    getAddressOptions(clientId) {
      return this.clientAddressOptions[clientId] || [];
    },

    isAddingAddress(clientId, type) {
      return (
        this.addingAddressFor &&
        this.addingAddressFor.clientId === clientId &&
        this.addingAddressFor.type === type
      );
    },

    startAddAddress(clientId, type) {
      this.addingAddressFor = { clientId, type };
      this.newAddress = { address1: "", address2: "", city: "", state: "", zip: "" };
    },

    cancelAddAddress() {
      this.addingAddressFor = null;
      this.newAddress = { address1: "", address2: "", city: "", state: "", zip: "" };
    },

    async saveNewAddress(task, type) {
      const clientId = task.clientId;
      this.savingAddress = true;
      try {
        const client = await clientsService.getById(clientId);
        const existingAddresses = client.addresses || [];
        const updatedAddresses = [
          ...existingAddresses,
          { ...this.newAddress },
        ];
        await clientsService.update(clientId, {
          ...client,
          addresses: updatedAddresses,
        });

        const refreshedClient = await clientsService.getById(clientId);
        const addrs =
          refreshedClient.addresses && refreshedClient.addresses.length > 0
            ? refreshedClient.addresses
            : [];
        const options = addrs.map((a) => ({
          id: a.id,
          name: this.formatAddressOption(a),
        }));
        this.$set(this.clientAddressOptions, clientId, options);

        if (options.length > 0) {
          const newAddrId = options[options.length - 1].id;
          if (type === "pickup") {
            task.pickupAddressId = newAddrId;
          } else {
            task.dropoffAddressId = newAddrId;
          }
        }

        this.cancelAddAddress();
        this.$notifySuccess("Address added successfully.");
      } catch (error) {
        this.$notifyError(
          "Error adding address: " +
            (error.response?.data?.error ||
              error.response?.data?.message ||
              error.message),
        );
      } finally {
        this.savingAddress = false;
      }
    },

    async loadClientAddresses() {
      const clientIds = [...new Set(this.clientTaskData.map((t) => t.clientId).filter(Boolean))];
      for (const clientId of clientIds) {
        try {
          const client = await clientsService.getById(clientId);
          const addrs = client.addresses && client.addresses.length > 0
            ? client.addresses
            : client.address
              ? [client.address]
              : [];
          const options = addrs.map((a) => ({
            id: a.id,
            name: this.formatAddressOption(a),
          }));
          this.$set(this.clientAddressOptions, clientId, options);

          // Auto-select the first (default) address for tasks that don't have one set
          if (options.length > 0) {
            const defaultId = options[0].id;
            this.clientTaskData.forEach((task) => {
              if (Number(task.clientId) === Number(clientId)) {
                if (!task.pickupAddressId) task.pickupAddressId = defaultId;
                if (!task.dropoffAddressId) task.dropoffAddressId = defaultId;
              }
            });
          }
        } catch (err) {
          this.$set(this.clientAddressOptions, clientId, []);
        }
      }
    },

    async fetchMedicationLogs() {
      const shiftId = this.shift.originalData?.id || this.shift.id;
      if (!shiftId) return;
      try {
        const response = await shiftsService.getShiftTasks(shiftId);
        const shiftData = response.data || response;
        if (shiftData.medicationAdministrationLogs) {
          const logsByClient = {};
          for (const log of shiftData.medicationAdministrationLogs) {
            const clientId = log.id_clients;
            if (!logsByClient[clientId]) logsByClient[clientId] = [];
            logsByClient[clientId].push(log);
          }
          this.medicationLogs = logsByClient;
        }
      } catch (err) {
        // silently handle error
      }
    },

    getClientMedicationLogs(clientId) {
      return this.medicationLogs[clientId] || [];
    },

    async loadQAData() {
      this.loadingQA = true;
      try {
        const shiftId = this.shift.originalData?.id || this.shift.id;

        // Load full shift data with answers via the tasks endpoint
        const fullShift = await shiftsService.getShiftTasks(shiftId);

        // Load service code questions
        const serviceCodes = fullShift?.service_codes || this.shift.service_codes || this.shift.originalData?.service_codes || [];
        const questionPromises = serviceCodes.map((sc) =>
          shiftNoteQuestionsService.getByServiceCode(sc.code || sc.id).catch(() => []),
        );
        const questionResults = await Promise.all(questionPromises);
        // Flatten and deduplicate by question id
        const allQuestions = [];
        const seenIds = new Set();
        for (const questions of questionResults) {
          for (const q of (questions || [])) {
            if (!seenIds.has(q.id)) {
              seenIds.add(q.id);
              allQuestions.push(q);
            }
          }
        }
        this.serviceCodeQuestions = allQuestions.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));

        // Build clientAnswers map from existing shift_note_answers and goal_answers
        const clientAnswersMap = {};
        for (const task of this.clientTaskData) {
          const cId = task.clientId;
          clientAnswersMap[cId] = {
            questionAnswers: {},
            goalAnswers: {},
            shiftNotes: "",
          };

          // Get shift notes text from client details
          const cd = (fullShift?.clientDetails || []).find(
            (d) => Number(d.id_clients) === Number(cId),
          );
          if (cd) {
            clientAnswersMap[cId].shiftNotes = cd.shift_notes || "";
          }
        }

        // Populate existing question answers from the shift data
        const shiftNoteAnswers = fullShift?.shiftNoteAnswers || [];
        for (const answer of shiftNoteAnswers) {
          const cId = answer.client_id;
          if (clientAnswersMap[cId]) {
            clientAnswersMap[cId].questionAnswers[answer.question_id] = answer.answer || "";
          }
        }

        // Populate existing goal answers from the shift data
        const existingGoalAnswers = fullShift?.goalAnswers || [];
        for (const answer of existingGoalAnswers) {
          const goalId = answer.id_goals;
          // Find which client this goal belongs to - we'll map by iterating clientTaskData
          // Goal answers don't have client_id directly, so we match via the goal's client
          for (const task of this.clientTaskData) {
            if (clientAnswersMap[task.clientId]) {
              clientAnswersMap[task.clientId].goalAnswers[goalId] = answer.answer || "";
            }
          }
        }

        this.clientAnswers = clientAnswersMap;

        // Load goals per client
        const goalsMap = {};
        const clientIds = this.clientTaskData.map((t) => t.clientId).filter(Boolean);
        const goalPromises = clientIds.map((cId) =>
          clientsService.getGoals(cId).then((response) => ({ cId, goals: response.goals || [] })).catch(() => ({ cId, goals: [] })),
        );
        const goalResults = await Promise.all(goalPromises);
        for (const { cId, goals } of goalResults) {
          goalsMap[cId] = goals;
        }
        this.clientGoals = goalsMap;

        // Now that we know which goals belong to which client, fix goal answer mapping
        // Goal answers should only appear under the client that owns the goal
        const goalToClient = {};
        for (const [cId, clientGoalsList] of Object.entries(goalsMap)) {
          for (const goal of clientGoalsList) {
            goalToClient[goal.id] = Number(cId);
          }
        }
        // Re-map goal answers correctly by client ownership
        for (const cId of Object.keys(clientAnswersMap)) {
          clientAnswersMap[cId].goalAnswers = {};
        }
        for (const answer of existingGoalAnswers) {
          const ownerClientId = goalToClient[answer.id_goals];
          if (ownerClientId && clientAnswersMap[ownerClientId]) {
            clientAnswersMap[ownerClientId].goalAnswers[answer.id_goals] = answer.answer || "";
          }
        }

        // Initialize expanded state
        const expanded = {};
        for (const task of this.clientTaskData) {
          expanded[task.clientId] = false;
        }
        this.expandedClients = expanded;
      } catch (err) {
      } finally {
        this.loadingQA = false;
      }
    },

    async loadVRQuestionData() {
      // Admin-configured shift-note questions for a VR 1-on-1 shift's service
      // codes (Settings -> Edit VR Shift Questions), with saved answers, so
      // they can be reviewed and edited after clock-out or approval. Mirrors
      // EditShiftNotesModal's loadVRNotes.
      if (this.vrQuestionsLoaded) return;
      this.vrQuestionsLoaded = true;

      this.vrQuestionAnswers = {};
      const serviceCodes =
        this.shift.service_codes || this.shift.originalData?.service_codes || [];
      if (serviceCodes.length === 0) {
        this.vrServiceCodeQuestions = [];
        return;
      }

      try {
        const results = await Promise.all(
          serviceCodes.map((sc) =>
            shiftNoteQuestionsService.getByServiceCode(sc.code).catch(() => []),
          ),
        );
        const all = [];
        const seen = new Set();
        for (const qs of results) {
          for (const q of qs || []) {
            if (!seen.has(q.id)) {
              seen.add(q.id);
              all.push(q);
            }
          }
        }
        this.vrServiceCodeQuestions = all.sort(
          (a, b) => (a.sort_order || 0) - (b.sort_order || 0),
        );
      } catch {
        this.vrServiceCodeQuestions = [];
      }
      if (this.vrServiceCodeQuestions.length === 0) return;

      // Seed saved answers (1-on-1 answers may lack a client_id; include those)
      try {
        const shiftId = this.shift.originalData?.id || this.shift.id;
        const clients = this.shift.clients || this.shift.originalData?.clients || [];
        const clientId = clients.length > 0 ? clients[0].id : null;
        const fullShift = await shiftsService.getShiftTasks(shiftId);
        const map = {};
        for (const answer of fullShift?.shiftNoteAnswers || []) {
          if (!answer.client_id || !clientId || Number(answer.client_id) === Number(clientId)) {
            let ans = answer.answer || "";
            if (ans.toLowerCase() === "yes" || ans.toLowerCase() === "no") {
              ans = ans.toLowerCase();
            }
            map[answer.question_id] = ans;
          }
        }
        this.vrQuestionAnswers = map;
      } catch (e) {
        // Questions still render; the admin can fill answers from scratch.
      }
    },

    async loadDSPDOneOnOneData() {
      if (this.dspdOneOnOneLoaded) return;
      this.dspdOneOnOneLoaded = true;

      try {
        const shiftId = this.shift.originalData?.id || this.shift.id;

        // Get the service code for this shift
        const serviceCodes = this.shift.service_codes || this.shift.originalData?.service_codes || [];
        const serviceCode = serviceCodes.length > 0 ? serviceCodes[0].code : null;

        // Get the client for this shift (1-on-1, so single client)
        const clients = this.shift.clients || this.shift.originalData?.clients || [];
        const clientId = clients.length > 0 ? clients[0].id : null;

        // Load service code questions
        if (serviceCode) {
          try {
            const questions = await shiftNoteQuestionsService.getByServiceCode(serviceCode);
            this.dspdOneOnOneQuestions = (questions || []).sort(
              (a, b) => (a.sort_order || 0) - (b.sort_order || 0),
            );
          } catch (e) {
            this.dspdOneOnOneQuestions = [];
          }
        }

        // Load client goals
        if (clientId) {
          try {
            const response = await clientsService.getGoals(clientId);
            this.dspdOneOnOneGoals = response.goals || [];
          } catch (e) {
            this.dspdOneOnOneGoals = [];
          }
        }

        // Load existing answers from the shift data
        const fullShift = await shiftsService.getShiftTasks(shiftId);

        // Populate question answers
        // For 1-on-1 shifts, include answers matching the client or without a client_id
        // Normalize yes/no answers to lowercase for consistent comparison
        const existingAnswers = fullShift?.shiftNoteAnswers || [];
        const qaMap = {};
        for (const answer of existingAnswers) {
          if (!answer.client_id || !clientId || Number(answer.client_id) === Number(clientId)) {
            let ans = answer.answer || "";
            if (ans.toLowerCase() === "yes" || ans.toLowerCase() === "no") {
              ans = ans.toLowerCase();
            }
            qaMap[answer.question_id] = ans;
          }
        }
        this.dspdOneOnOneQuestionAnswers = qaMap;

        // Populate goal answers
        // Normalize yes/no answers to lowercase for consistent comparison
        const existingGoalAnswers = fullShift?.goalAnswers || [];
        const gaMap = {};
        for (const answer of existingGoalAnswers) {
          let ans = answer.answer || "";
          if (ans.toLowerCase() === "yes" || ans.toLowerCase() === "no") {
            ans = ans.toLowerCase();
          }
          gaMap[answer.id_goals] = ans;
        }
        this.dspdOneOnOneGoalAnswers = gaMap;

        // Load concerns (shift_notes)
        this.dspdOneOnOneConcerns = fullShift?.shift_notes || this.shift.originalData?.shift_notes || "";
      } catch (err) {
        // silently handle error
      }
    },

    toggleClientQA(clientId) {
      this.$set(this.expandedClients, clientId, !this.expandedClients[clientId]);
    },

    getClientGoals(clientId) {
      return this.clientGoals[clientId] || [];
    },

    // multi_select answers are stored as a comma-joined string; convert
    // to/from the array the multi-select control expects.
    parseMultiAnswer(value) {
      if (Array.isArray(value)) return value;
      if (value === null || value === undefined || value === "") return [];
      return String(value)
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
    },
    joinMultiAnswer(value) {
      return (Array.isArray(value) ? value : []).join(", ");
    },

    getQuestionAnswer(clientId, questionId) {
      return this.clientAnswers[clientId]?.questionAnswers?.[questionId] || "";
    },

    setQuestionAnswer(clientId, questionId, value) {
      if (!this.clientAnswers[clientId]) {
        this.$set(this.clientAnswers, clientId, { questionAnswers: {}, goalAnswers: {}, shiftNotes: "" });
      }
      if (!this.clientAnswers[clientId].questionAnswers) {
        this.$set(this.clientAnswers[clientId], "questionAnswers", {});
      }
      this.$set(this.clientAnswers[clientId].questionAnswers, questionId, value);
    },

    getGoalAnswer(clientId, goalId) {
      return this.clientAnswers[clientId]?.goalAnswers?.[goalId] || "";
    },

    setGoalAnswer(clientId, goalId, value) {
      if (!this.clientAnswers[clientId]) {
        this.$set(this.clientAnswers, clientId, { questionAnswers: {}, goalAnswers: {}, shiftNotes: "" });
      }
      if (!this.clientAnswers[clientId].goalAnswers) {
        this.$set(this.clientAnswers[clientId], "goalAnswers", {});
      }
      this.$set(this.clientAnswers[clientId].goalAnswers, goalId, value);
    },

    getClientShiftNotes(clientId) {
      return this.clientAnswers[clientId]?.shiftNotes || "";
    },

    setClientShiftNotes(clientId, value) {
      if (!this.clientAnswers[clientId]) {
        this.$set(this.clientAnswers, clientId, { questionAnswers: {}, goalAnswers: {}, shiftNotes: "" });
      }
      this.$set(this.clientAnswers[clientId], "shiftNotes", value);
    },

    isClientNotesComplete(clientId) {
      const answers = this.clientAnswers[clientId];
      if (!answers) return false;

      // Concerns question must be answered
      if (!answers.shiftNotes || !answers.shiftNotes.trim()) return false;

      // All service code questions must be answered
      for (const question of this.serviceCodeQuestions) {
        const answer = answers.questionAnswers?.[question.id];
        if (!answer || !answer.toString().trim()) return false;
      }

      // All client goals must be answered
      const goals = this.getClientGoals(clientId);
      for (const goal of goals) {
        const answer = answers.goalAnswers?.[goal.id];
        if (!answer || !answer.toString().trim()) return false;
      }

      return true;
    },

    formatActualTimeRange(shift) {
      // Handle both snake_case and camelCase formats
      const clockIn =
        shift.originalData?.clock_in ||
        shift.originalData?.clockIn ||
        shift.actualStartTime;
      const clockOut =
        shift.originalData?.clock_out ||
        shift.originalData?.clockOut ||
        shift.actualEndTime;

      if (!clockIn && !clockOut) {
        return "Not clocked in";
      }

      const clockInTime = clockIn ? this.formatClockTime(clockIn) : "";
      const clockOutTime = clockOut ? this.formatClockTime(clockOut) : "";

      if (clockIn && clockOut) {
        return `${clockInTime} - ${clockOutTime}`;
      } else if (clockIn) {
        return `${clockInTime} - In Progress`;
      } else {
        return "Not clocked in";
      }
    },
  },
};
</script>

<style scoped>
.edit-clock-out-content {
  max-width: 700px;
  min-width: 600px;
  margin: 0 auto;
  padding: 0 2rem;
}

.edit-clock-out-content .mb-3 {
  margin-bottom: 1.5rem;
}

.edit-clock-out-content h5 {
  color: #fff;
  margin-bottom: 1rem;
  font-weight: 600;
  border-bottom: 2px solid #6ba6cd;
  padding-bottom: 0.5rem;
  font-size: 1.1rem;
}

.edit-clock-out-content p {
  margin-bottom: 0.5rem;
  color: #e0e0e0;
  font-size: 0.95rem;
}

.edit-clock-out-content strong {
  color: #fff;
  font-weight: 600;
}

.form-label {
  color: #fff;
  font-weight: 600;
  margin-bottom: 0.5rem;
  display: block;
  font-size: 0.95rem;
}

.time-input-container {
  margin-bottom: 0.5rem;
}

.clock-time-picker {
  width: 100%;
}

.text-center {
  text-align: center;
}

/* Status badge styling matching ShiftActionModal */
.status-badge {
  display: inline-block !important;
  padding: 0.35rem 0.75rem !important;
  font-size: 0.85rem !important;
  font-weight: 500 !important;
  border-radius: 1rem !important;
  text-transform: uppercase !important;
  letter-spacing: 0.5px !important;
  border: 1px solid rgba(255, 255, 255, 0.2) !important;
}

.status-badge.badge-secondary {
  background: linear-gradient(
    135deg,
    rgba(220, 53, 69, 0.7),
    rgba(220, 53, 69, 0.5)
  ) !important;
  color: #fff !important;
}

.status-badge.badge-info {
  background: linear-gradient(
    135deg,
    rgba(13, 110, 253, 0.8),
    rgba(13, 110, 253, 0.6)
  ) !important;
  color: #fff !important;
}

.status-badge.badge-success {
  background: linear-gradient(
    135deg,
    rgba(255, 193, 7, 0.9),
    rgba(255, 193, 7, 0.7)
  ) !important;
  color: #000 !important;
}

.status-badge.badge-warning {
  background: linear-gradient(
    135deg,
    rgba(237, 137, 54, 0.7),
    rgba(237, 137, 54, 0.5)
  ) !important;
  color: #fff !important;
}

.status-badge.badge-primary {
  background: linear-gradient(
    135deg,
    rgba(156, 39, 176, 0.7),
    rgba(156, 39, 176, 0.5)
  ) !important;
  color: #fff !important;
}

.status-badge.badge-danger {
  background: linear-gradient(
    135deg,
    rgba(220, 53, 69, 0.8),
    rgba(220, 53, 69, 0.6)
  ) !important;
  color: #fff !important;
}

/* Make placeholder text darker when textarea is focused/selected */
:deep(.el-textarea__inner:focus::placeholder) {
  color: #999 !important;
}

.service-code-warning {
  margin-top: 0.5rem;
}

.service-code-warning .text-warning {
  color: #ffc107 !important;
}

.service-code-warning i {
  margin-right: 0.25rem;
}

.break-duration-display {
  margin-top: 0.75rem;
  padding: 0.5rem;
  background-color: rgba(255, 255, 255, 0.05);
  border-radius: 4px;
  color: #fff;
  font-size: 0.9rem;
}

/* Section headings for group shift sections */
.edit-clock-out-content .mb-4 {
  margin-bottom: 2rem;
}

.edit-clock-out-content h5 {
  color: #fff;
  margin-bottom: 1rem;
  font-weight: 600;
  border-bottom: 2px solid #6ba6cd;
  padding-bottom: 0.5rem;
  font-size: 1.1rem;
}

/* Caregiver Clock Times */
.caregiver-clock-times {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.caregiver-clock-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 0.75rem 1rem;
}

.caregiver-name {
  font-weight: 600;
  color: #fff;
  min-width: 140px;
}

.caregiver-time-inputs {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.caregiver-time-field {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.clock-label {
  color: #909399;
  font-size: 0.85rem;
  white-space: nowrap;
}

.clock-time-picker-sm {
  width: 140px;
}

/* Client Tasks */
.client-tasks-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.client-task-card {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 1rem;
}

.client-task-header {
  font-weight: 600;
  color: #fff;
  font-size: 1rem;
  margin-bottom: 0.75rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.did-not-attend-badge {
  font-size: 0.8rem;
  font-weight: 500;
  color: #f56c6c;
  background: rgba(245, 108, 108, 0.15);
  padding: 0.15rem 0.5rem;
  border-radius: 4px;
}

.client-task-rows {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.client-task-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0.75rem;
  background: rgba(0, 0, 0, 0.2);
  border-radius: 6px;
}

.client-task-info {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: #fff;
}

.client-task-info i {
  font-size: 14px;
  color: #909399;
  width: 18px;
  text-align: center;
}

.client-task-label {
  font-weight: 500;
  font-size: 0.9rem;
}

.client-task-controls {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.client-task-controls.location-controls {
  flex: 1;
  margin-left: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.location-row {
  flex-wrap: wrap;
}

.add-address-btn {
  color: #6ba6cd !important;
  padding: 4px 8px !important;
  white-space: nowrap;
}

.add-address-btn:hover {
  color: #8fc0e0 !important;
}

.new-address-form {
  width: 100%;
  padding: 0.75rem;
  margin-top: 0.5rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(107, 166, 205, 0.3);
  border-radius: 6px;
}

.address-form-row {
  margin-bottom: 0.5rem;
}

.address-form-row.three-col {
  display: flex;
  gap: 0.5rem;
}

.address-form-row.three-col .state-input {
  max-width: 80px;
}

.address-form-row.three-col .zip-input {
  max-width: 100px;
}

.address-form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 0.5rem;
}

.client-task-completed {
  color: #67c23a;
  font-size: 0.85rem;
}

.client-task-pending {
  color: #909399;
  font-size: 0.85rem;
  font-style: italic;
}

@media (max-width: 768px) {
  /* Make modal wider on mobile devices */
  :deep(.el-dialog) {
    width: 95% !important;
    margin: 5px auto !important;
  }

  .edit-clock-out-content {
    min-width: auto !important;
    padding: 0 1rem;
  }

  .caregiver-clock-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }

  .caregiver-time-inputs {
    flex-direction: column;
    width: 100%;
    gap: 0.5rem;
  }

  .caregiver-time-field {
    width: 100%;
  }

  .clock-time-picker-sm {
    width: 100%;
  }

  .client-task-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }

  .client-task-controls {
    flex-direction: column;
    align-items: flex-start;
    width: 100%;
    gap: 0.5rem;
  }
}

/* Inline Shift Notes */
.client-notes-inline {
  margin-top: 0.75rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  padding-top: 0.75rem;
}

.required-indicator {
  color: #f44336;
  margin-left: 2px;
}

.yes-no-buttons {
  display: flex;
  gap: 0.5rem;
}
</style>
